<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'
import { RouterView } from 'vue-router'
import { useAuthStore, authService, ConfirmDialog, useWebSocketLifecycle } from '@adaptcx/shared'
import { useDarsEvents } from '@/composables/useDarsEvents'

const authStore = useAuthStore()
let stopSessionWatcher: (() => void) | null = null

// v0.9.42 — Phase 3 of DARS WebSocket consolidation. The shared platform
// /ws connection is driven by useWebSocketLifecycle, which watches Pinia
// useAuthTokenStore.accessToken and connects/disconnects as the token
// appears/clears. Fixes the "fresh login gets no WS" bug: onMounted here
// only ran once at initial App.vue mount, before the token existed on a
// fresh login. useDarsEvents() below registers DARS-domain event handlers
// on the shared connection.
useWebSocketLifecycle()
useDarsEvents()

// Editorial portal — single-theme (paper-light). Dark-mode strip
// happens in index.html so useTheme does not re-add .dark on mount.

onMounted(async () => {
  authStore.initFromStorage()
  const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token')
  if (token) {
    try {
      await authStore.fetchUser()
      stopSessionWatcher = authService.startSessionExpiryWatcher()
    } catch (e) {
      console.error('Failed to restore auth state:', e)
    }
  }
})

onUnmounted(() => {
  if (stopSessionWatcher) {
    stopSessionWatcher()
    stopSessionWatcher = null
  }
})
</script>

<template>
  <RouterView />
  <!-- v0.8.43 — remote-audio sink for Telnyx WebRTC. The shared
       useTelnyxWebRTC composable points client.remoteElement at this
       element by ID; without it the SDK connects but no audio plays.
       Hidden but rendered; autoplay is required for the SDK to attach
       the incoming MediaStream. -->
  <audio id="telnyx-remote-audio" autoplay />
  <!-- v0.9.0 — global confirm dialog. Any view can trigger it via
       useConfirm().confirm({...}). Replaces every native
       window.confirm() usage per feedback_no_native_alert_confirm. -->
  <ConfirmDialog />
</template>
